import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Typography,
  keyframes,
} from "@mui/material";
import MeetingModal from "./MeetingModal";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import GoogleTranslateButton from "./GoogleTranslateButton";

// ============ ANIMATIONS (Only for interactions, not entrance) ============
const slideInDrawer = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;


// Navigation items data
const navItems = [
  { label: "Intro", path: "/" },
  { label: "User Journey", path: "/user-journey" },
  { 
    label: "Smart Reporting", 
    path: "/smartReporting",
    hasDropdown: true,
    dropdownItems: [
      { label: "Smart Report", path: "/smartReport" },
      { label: "Viz App", path: "/vizApp" },
    ]
  },
  { label: "Engagement", path: "/engagement" },
  { label: "Testimonials", path: "/testimonials" },
  { label: "ROI Calculator", path: "/roi-calculator" },
];

// Custom Hamburger Icon Component
const HamburgerIcon = ({ isOpen, onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      width: "44px",
      height: "44px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "6px",
      padding: "10px",
      borderRadius: "12px",
      backgroundColor: isOpen ? "rgba(64, 123, 255, 0.1)" : "transparent",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "rgba(64, 123, 255, 0.1)",
      },
    }}
  >
    {/* Top line */}
    <Box
      sx={{
        width: "22px",
        height: "2.5px",
        backgroundColor: "#1D47A4",
        borderRadius: "2px",
        transformOrigin: "center",
        transition: "transform 0.3s ease",
        transform: isOpen ? "translateY(8.5px) rotate(45deg)" : "translateY(0) rotate(0)",
      }}
    />
    {/* Middle line */}
    <Box
      sx={{
        width: "22px",
        height: "2.5px",
        backgroundColor: "#1D47A4",
        borderRadius: "2px",
        transition: "all 0.3s ease",
        opacity: isOpen ? 0 : 1,
        transform: isOpen ? "scaleX(0)" : "scaleX(1)",
      }}
    />
    {/* Bottom line */}
    <Box
      sx={{
        width: "22px",
        height: "2.5px",
        backgroundColor: "#1D47A4",
        borderRadius: "2px",
        transformOrigin: "center",
        transition: "transform 0.3s ease",
        transform: isOpen ? "translateY(-8.5px) rotate(-45deg)" : "translateY(0) rotate(0)",
      }}
    />
  </IconButton>
);

// Navigation Link Component
const NavLink = ({ item, isActive, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: "relative",
      cursor: "pointer",
      padding: "8px 0",
    }}
  >
    <Typography
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "15px",
        fontWeight: isActive ? 600 : 500,
        color: isActive ? "#1D47A4" : "#4B5563",
        transition: "all 0.3s ease",
        letterSpacing: "0.2px",
        "&:hover": {
          color: "#1D47A4",
        },
      }}
    >
      {item.label}
    </Typography>
    {/* Animated underline */}
    <Box
      sx={{
        position: "absolute",
        bottom: "4px",
        left: "0",
        right: "0",
        height: "2.5px",
        backgroundColor: "#407BFF",
        borderRadius: "2px",
        transform: isActive ? "scaleX(1)" : "scaleX(0)",
        transformOrigin: "left center",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    />
  </Box>
);

// Dropdown Navigation Link Component
const DropdownNavLink = ({ item, isActive, onNavigate, pathname }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mouseLeaveTimeout, setMouseLeaveTimeout] = useState(null);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (mouseLeaveTimeout) {
      clearTimeout(mouseLeaveTimeout);
      setMouseLeaveTimeout(null);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Add a delay before closing to allow moving to menu
    const timeout = setTimeout(() => {
      setIsOpen(false);
    }, 200);
    setMouseLeaveTimeout(timeout);
  };

  const handleMenuMouseEnter = () => {
    // Clear close timeout when mouse enters menu
    if (mouseLeaveTimeout) {
      clearTimeout(mouseLeaveTimeout);
      setMouseLeaveTimeout(null);
    }
    setIsOpen(true);
  };

  const handleMenuMouseLeave = () => {
    setIsOpen(false);
  };

  const handleMenuItemClick = (path) => {
    onNavigate(path);
    setIsOpen(false);
    if (mouseLeaveTimeout) {
      clearTimeout(mouseLeaveTimeout);
      setMouseLeaveTimeout(null);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mouseLeaveTimeout) {
        clearTimeout(mouseLeaveTimeout);
      }
    };
  }, [mouseLeaveTimeout]);

  const handleLabelClick = (e) => {
    // Navigate to the main path when label is clicked
    e.stopPropagation();
    onNavigate(item.path);
    setIsOpen(false);
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: "relative",
        cursor: "pointer",
        padding: "8px 0",
      }}
    >
      <Box
        onClick={handleLabelClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "15px",
            fontWeight: isActive ? 600 : 500,
            color: isActive ? "#1D47A4" : "#4B5563",
            transition: "all 0.3s ease",
            letterSpacing: "0.2px",
            "&:hover": {
              color: "#1D47A4",
            },
          }}
        >
          {item.label}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: "16px",
            color: isActive ? "#1D47A4" : "#4B5563",
            transition: "transform 0.3s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      {/* Animated underline */}
      <Box
        sx={{
          position: "absolute",
          bottom: "4px",
          left: "0",
          right: "0",
          height: "2.5px",
          backgroundColor: "#407BFF",
          borderRadius: "2px",
          transform: isActive ? "scaleX(1)" : "scaleX(0)",
          transformOrigin: "left center",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
      
      {/* Custom Dropdown Menu */}
      <Box
        onMouseEnter={handleMenuMouseEnter}
        onMouseLeave={handleMenuMouseLeave}
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          marginTop: "8px",
          minWidth: "200px",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(64, 123, 255, 0.1)",
          padding: "8px 0",
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(20px)",
          zIndex: 1400,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? "visible" : "hidden",
          transform: isOpen ? "translateY(0)" : "translateY(-10px)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: isOpen ? "auto" : "none",
        }}
      >
        {item.dropdownItems?.map((dropdownItem) => {
          const isDropdownActive = pathname === dropdownItem.path;
          return (
            <Box
              key={dropdownItem.path}
              onClick={() => handleMenuItemClick(dropdownItem.path)}
              sx={{
                padding: "10px 20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                fontWeight: isDropdownActive ? 600 : 500,
                color: isDropdownActive ? "#1D47A4" : "#4B5563",
                backgroundColor: isDropdownActive
                  ? "rgba(64, 123, 255, 0.08)"
                  : "transparent",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(64, 123, 255, 0.1)",
                  color: "#1D47A4",
                },
              }}
            >
              {dropdownItem.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Mobile Drawer Item Component
const MobileDrawerItem = ({ item, pathname, onNavigate, index }) => {
  const [expanded, setExpanded] = useState(false);
  const isItemActive =
    pathname === item.path ||
    (item.hasDropdown &&
      item.dropdownItems?.some((di) => di.path === pathname));

  return (
    <Box>
      <ListItem
        onClick={() => {
          // For all items (including those with dropdown), tapping the row
          // should navigate to the main path, similar to desktop behavior.
          onNavigate(item.path);
        }}
        sx={{
          padding: { xs: "12px 16px", sm: "16px 20px" },
          marginBottom: { xs: "6px", sm: "8px" },
          borderRadius: { xs: "12px", sm: "16px" },
          cursor: "pointer",
          backgroundColor: isItemActive
            ? "rgba(64, 123, 255, 0.08)"
            : "transparent",
          border: isItemActive
            ? "1px solid rgba(64, 123, 255, 0.15)"
            : "1px solid transparent",
          transition: "all 0.3s ease",
          animation: `${slideInDrawer} 0.3s ease ${index * 0.05}s both`,
          "&:active": {
            backgroundColor: "rgba(64, 123, 255, 0.12)",
            transform: "scale(0.98)",
          },
        }}
      >
        {/* Active indicator - subtle dot instead of bar */}
        <Box
          sx={{
            width: { xs: "6px", sm: "8px" },
            height: { xs: "6px", sm: "8px" },
            backgroundColor: isItemActive ? "#407BFF" : "transparent",
            borderRadius: "50%",
            marginRight: { xs: "12px", sm: "16px" },
            flexShrink: 0,
            transition: "all 0.3s ease",
          }}
        />
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "15px", sm: "17px" },
            fontWeight: isItemActive ? 600 : 500,
            color: isItemActive ? "#1D47A4" : "#374151",
            letterSpacing: "0.2px",
            lineHeight: 1.4,
          }}
        />
        {item.hasDropdown && (
          <KeyboardArrowDownIcon
            onClick={(e) => {
              // Allow expanding/collapsing without triggering navigation
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            sx={{
              fontSize: "18px",
              color: "#4B5563",
              transition: "transform 0.3s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              cursor: "pointer",
            }}
          />
        )}
      </ListItem>
      {/* Dropdown items */}
      {item.hasDropdown && expanded && (
        <Box sx={{ pl: { xs: "28px", sm: "36px" } }}>
          {item.dropdownItems?.map((dropdownItem, subIndex) => {
            const isDropdownActive = pathname === dropdownItem.path;
            return (
              <ListItem
                key={dropdownItem.path}
                onClick={() => onNavigate(dropdownItem.path)}
                sx={{
                  padding: { xs: "10px 16px", sm: "12px 20px" },
                  marginBottom: { xs: "4px", sm: "6px" },
                  borderRadius: { xs: "10px", sm: "12px" },
                  cursor: "pointer",
                  backgroundColor: isDropdownActive
                    ? "rgba(64, 123, 255, 0.12)"
                    : "rgba(64, 123, 255, 0.04)",
                  border: isDropdownActive
                    ? "1px solid rgba(64, 123, 255, 0.2)"
                    : "1px solid transparent",
                  transition: "all 0.3s ease",
                  animation: `${slideInDrawer} 0.3s ease ${(index + subIndex + 1) * 0.05}s both`,
                  "&:active": {
                    backgroundColor: "rgba(64, 123, 255, 0.15)",
                    transform: "scale(0.98)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: { xs: "4px", sm: "6px" },
                    height: { xs: "4px", sm: "6px" },
                    backgroundColor: isDropdownActive
                      ? "#407BFF"
                      : "transparent",
                    borderRadius: "50%",
                    marginRight: { xs: "10px", sm: "12px" },
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                  }}
                />
                <ListItemText
                  primary={dropdownItem.label}
                  primaryTypographyProps={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: { xs: "14px", sm: "15px" },
                    fontWeight: isDropdownActive ? 600 : 500,
                    color: isDropdownActive ? "#1D47A4" : "#6B7280",
                    letterSpacing: "0.2px",
                    lineHeight: 1.4,
                  }}
                />
              </ListItem>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const Navbar = ({ transparent = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled 
            ? "rgba(255, 255, 255, 0.95)" 
            : transparent 
              ? "transparent" 
              : "rgba(255, 255, 255, 0.98)",
          backdropFilter: scrolled ? "blur(20px)" : "blur(10px)",
          borderBottom: scrolled 
            ? "1px solid rgba(64, 123, 255, 0.08)" 
            : "1px solid transparent",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: scrolled 
            ? "0 4px 30px rgba(0, 0, 0, 0.05)" 
            : "none",
          zIndex: 1100,
          overflow: "visible",
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: "70px", md: "80px" },
            padding: { 
              xs: "0 16px", 
              sm: "0 24px", 
              md: "0 48px", 
              lg: "0 64px" 
            },
            maxWidth: "1440px",
            width: "100%",
            margin: "0 auto",
          }}
        >
          {/* Logo */}
          <Box
            onClick={() => handleNavigation("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              flexShrink: 0,
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
          >
            <img
              src="/Niroggyan_logo.svg"
              alt="Niroggyan logo"
              style={{
                width: isMobile ? "120px" : "160px",
                height: "auto",
                transition: "all 0.3s ease",
              }}
            />
          </Box>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { md: "32px", lg: "40px" },
                position: "relative",
                zIndex: 1,
              }}
            >
              {navItems.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <DropdownNavLink
                      key={item.path}
                      item={item}
                      isActive={
                        pathname === item.path ||
                        item.dropdownItems?.some((di) => di.path === pathname)
                      }
                      onNavigate={handleNavigation}
                      pathname={pathname}
                    />
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    item={item}
                    isActive={pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
                  />
                );
              })}

              {/* Translation Button - positioned where Schedule Demo was */}
              {/* <GoogleTranslateButton /> */}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Translation Button */}
              {/* <GoogleTranslateButton /> */}
              
              <HamburgerIcon 
                isOpen={drawerOpen} 
                onClick={toggleDrawer}
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "340px" },
            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
            borderRadius: { xs: "0", sm: "24px 0 0 24px" },
            boxShadow: "-10px 0 40px rgba(0, 0, 0, 0.1)",
          },
        }}
        SlideProps={{
          timeout: 400,
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "16px 20px", sm: "20px 24px" },
            borderBottom: "1px solid rgba(64, 123, 255, 0.1)",
          }}
        >
          <Box
            component="img"
            src="/Niroggyan_logo.svg"
            alt="Niroggyan logo"
            sx={{
              width: { xs: "100px", sm: "120px" },
              height: "auto",
            }}
          />
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{
              width: { xs: "36px", sm: "40px" },
              height: { xs: "36px", sm: "40px" },
              backgroundColor: "rgba(64, 123, 255, 0.08)",
              borderRadius: "10px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(64, 123, 255, 0.15)",
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon sx={{ color: "#1D47A4", fontSize: { xs: "18px", sm: "20px" } }} />
          </IconButton>
        </Box>

        {/* Navigation Links */}
        <List 
          sx={{ 
            padding: { xs: "16px 12px", sm: "24px 16px" }, 
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          {navItems.map((item, index) => (
            <MobileDrawerItem
              key={item.path}
              item={item}
              pathname={pathname}
              onNavigate={handleNavigation}
              index={index}
            />
          ))}
        </List>

        {/* Drawer Footer - CTA */}
        <Box
          sx={{
            padding: { xs: "16px 20px 20px", sm: "24px" },
            borderTop: "1px solid rgba(64, 123, 255, 0.1)",
            backgroundColor: "rgba(248, 250, 255, 0.5)",
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              setShowMeeting(true);
              setDrawerOpen(false);
            }}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 600,
              color: "#fff",
              background: "linear-gradient(135deg, #407BFF 0%, #1D47A4 100%)",
              padding: { xs: "12px 20px", sm: "16px 24px" },
              borderRadius: { xs: "12px", sm: "16px" },
              textTransform: "none",
              boxShadow: "0 4px 16px rgba(64, 123, 255, 0.3)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            Schedule a Demo
          </Button>
          
          {/* Contact info - more compact for mobile */}
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "11px", sm: "13px" },
              color: "#6B7280",
              textAlign: "center",
              marginTop: { xs: "12px", sm: "16px" },
              lineHeight: 1.5,
            }}
          >
            Or email us at{" "}
            <Box
              component="a"
              href="mailto:contact@niroggyan.com"
              sx={{
                color: "#407BFF",
                textDecoration: "none",
                fontWeight: 500,
                fontSize: { xs: "11px", sm: "13px" },
                "&:active": {
                  opacity: 0.8,
                },
              }}
            >
              contact@niroggyan.com
            </Box>
          </Typography>
        </Box>

        {/* Decorative elements - hidden on mobile for cleaner look */}
        <Box
          sx={{
            position: "absolute",
            bottom: "150px",
            right: "-50px",
            width: "150px",
            height: "150px",
            background: "radial-gradient(circle, rgba(64, 123, 255, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
            display: { xs: "none", sm: "block" },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "100px",
            left: "-30px",
            width: "100px",
            height: "100px",
            background: "radial-gradient(circle, rgba(64, 123, 255, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
            display: { xs: "none", sm: "block" },
          }}
        />
      </Drawer>

      {/* Meeting Modal */}
      <MeetingModal showMeeting={showMeeting} setShowMeeting={setShowMeeting} />

      {/* Spacer to prevent content from going under fixed navbar */}
      <Box sx={{ height: { xs: "70px", md: "80px" } }} />
    </>
  );
};

export default Navbar;
